import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Mail, Phone, MapPin, CheckCircle, Calendar
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast, Toaster } from 'sonner';
import { handleDemoRequest } from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactPage = () => {
  // CMS settings state
  const [cmsSettings, setCmsSettings] = useState(null);
  
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    staffSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch CMS settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cms/contact-settings`);
        if (response.ok) {
          const data = await response.json();
          setCmsSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch contact settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Get CMS values with fallbacks
  const heroTitle = cmsSettings?.hero_title || 'Get in Touch';
  const heroSubtitle = cmsSettings?.hero_subtitle || 'Ready to transform your care operations? Book a strategy call or send us a message. Our Australian team is here to help.';
  const contactEmail = cmsSettings?.email || 'info@getcare360.com.au';
  const contactPhone = cmsSettings?.phone || '1800 951 GET';
  const contactAddress = cmsSettings?.address || 'Australia Wide Service';
  const officeHours = cmsSettings?.office_hours || 'Monday - Friday: 9:00 AM - 5:00 PM AEST';
  const responseTime = cmsSettings?.response_time || 'We typically respond within 24 hours';
  const ctaTitle = cmsSettings?.cta_title || 'Prefer to Book a Call?';
  const ctaSubtitle = cmsSettings?.cta_subtitle || 'Schedule a free strategy session with our NDIS software experts';
  const ctaButtonText = cmsSettings?.cta_button_text || 'Book Free Strategy Call';

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await handleDemoRequest(demoForm);
      toast.success(result.message);
      setDemoForm({ name: '', email: '', company: '', phone: '', staffSize: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Contact Us | GetCare 360 - Reach Our Australian NDIS Software Team"
        description="Get in touch with the GetCare 360 team. Book a free strategy call, request a demo, or ask questions about our NDIS software solutions. Australian-based support."
        keywords="contact GetCare 360, NDIS software support Australia, book demo NDIS software, NDIS provider help"
        canonicalUrl="/contact"
        page="contact"
      />
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-deep-navy mb-6">
              {heroTitle.includes('Touch') ? (
                <>Get in <span className="text-primary-blue">Touch</span></>
              ) : (
                heroTitle
              )}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-light-grey hover:border-primary-blue transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-primary-blue/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-primary-blue" size={24} />
                </div>
                <h3 className="font-semibold text-deep-navy mb-2">Email Us</h3>
                <a href={`mailto:${contactEmail}`} className="text-primary-blue hover:underline">
                  {contactEmail}
                </a>
              </CardContent>
            </Card>
            <Card className="border-2 border-light-grey hover:border-primary-blue transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-primary-blue/10 flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-primary-blue" size={24} />
                </div>
                <h3 className="font-semibold text-deep-navy mb-2">Call Us</h3>
                <a href="tel:1800951438" className="text-primary-blue hover:underline">
                  {contactPhone}
                </a>
              </CardContent>
            </Card>
            <Card className="border-2 border-light-grey hover:border-primary-blue transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-primary-blue/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-primary-blue" size={24} />
                </div>
                <h3 className="font-semibold text-deep-navy mb-2">Location</h3>
                <p className="text-gray-600">{contactAddress}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Demo Booking Form */}
      <section id="demo" className="py-20 bg-deep-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-teal/20 rounded-full border border-teal/30 mb-6">
              <Calendar className="text-teal mr-2" size={20} />
              <span className="text-sm font-medium">Executive Strategy Session</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Book Your <span className="text-teal">Strategy Call</span>
            </h2>
            <p className="text-xl text-white/80">
              We'll create a custom AI system blueprint for your provider during a 30-minute strategy session.
            </p>
          </div>

          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleDemoSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-teal"
                      placeholder="John Smith"
                      data-testid="demo-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-teal"
                      placeholder="john@example.com"
                      data-testid="demo-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company/Organisation *</label>
                    <input
                      type="text"
                      required
                      value={demoForm.company}
                      onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-teal"
                      placeholder="Your Organisation"
                      data-testid="demo-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={demoForm.phone}
                      onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-teal"
                      placeholder="0400 000 000"
                      data-testid="demo-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Staff Size *</label>
                  <select
                    required
                    value={demoForm.staffSize}
                    onChange={(e) => setDemoForm({ ...demoForm, staffSize: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-teal"
                    data-testid="demo-staff-select"
                  >
                    <option value="" className="bg-deep-navy">Select staff size</option>
                    <option value="5-15" className="bg-deep-navy">5-15 Staff</option>
                    <option value="15-50" className="bg-deep-navy">15-50 Staff</option>
                    <option value="50+" className="bg-deep-navy">50+ Staff</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Tell us about your needs</label>
                  <textarea
                    value={demoForm.message}
                    onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-teal resize-none"
                    placeholder="Tell us about your current challenges and what you're looking for..."
                    data-testid="demo-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal hover:bg-teal/90 text-white py-6 text-lg h-auto"
                  data-testid="demo-submit-btn"
                >
                  {isSubmitting ? 'Booking...' : 'Book Executive Strategy Call'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-white/70">
              What happens next? Our team will contact you within 24 hours to schedule your strategy session.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-deep-navy mb-6">
              Why Work With <span className="text-primary-blue">GetCare?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Australian Team', desc: 'Local support from experts who understand NDIS' },
              { title: 'Custom Built', desc: 'Platforms designed for your specific workflow' },
              { title: 'AI Powered', desc: 'Intelligent automation and insights' },
              { title: 'Ongoing Support', desc: 'We\'re with you for the long term' }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-light-grey text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-teal" size={24} />
                  </div>
                  <h3 className="font-semibold text-deep-navy mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
